/* THE CALIBRATION GRID — a flat, camera-facing reference plane over a
   corner screen's shared seam, On-site only.

   The point of it is narrow: doSolve (core/calibrate.js) recovers a focal
   length and an eye position from four marked corners, and a wrong solve
   still draws a perfectly normal-looking picture — nothing about the
   render itself says whether the numbers underneath it are right. This
   gives that a visual answer. A plane that stays flat and square BY
   CONSTRUCTION, held up facing the calibrated eye, either looks flat and
   square through that solve or it doesn't. It can't repeat the solve's own
   error the way a grid painted onto the two panels' own planes would —
   that grid would warp exactly as much as the solve says it should and
   call it correct, which is the one thing it must never do.

   Billboarded rather than fixed to either panel's plane, and that's what
   makes it work across the corner at all: On-site renders the deliverable
   in two passes, one per panel, each through that panel's own off-axis
   camera (flatCam, cornerCam — see scene/cameras.js's updateScreenCam).
   Those two cameras sit at the SAME world position — one calibrated eye,
   sheared two different ways — so a rotation built from camera POSITION
   alone (exactly what applyBillboards already does for Face-the-camera
   objects, scene/renderer.js) comes out identical in both passes. The
   grid is one real plane in one real orientation; each pass's own
   clipping (applyScreenClip) keeps its own half, the same mechanism that
   already lets any object spanning the corner render correctly through
   both cones. No per-panel projection to build — it rides the one the app
   already has.

   Reading it: the grid should look flat and square. If it's skewed, the
   first fix is the camera calibration itself — re-lock the angle. If a
   re-lock doesn't straighten it, the next suspect is the typed screen
   dimensions (S.screen.w/h, S.cornerPanel.w) — a wrong size warps every
   solve the same way, however carefully the corners were marked, because
   doSolve has no way to tell a mis-typed screen from a correctly-marked
   one. Same guidance is on the toggle button's tooltip; see it there for
   the user-facing wording. */
import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { scene } from './context.js';
import { S } from '../state/store.js';
import { isSiteLike } from '../scene/cameras.js';

/* Fat lines (three's own LineSegments2/LineMaterial addon), the same reason
   scene/helpers.js's selBox and eyeCam use it over a plain LineBasicMaterial:
   native WebGL line width is not honoured by ANGLE, so a plain line is 1px
   everywhere that matters regardless of what's asked for. resolution is kept
   in sync by scene/renderer.js's syncFatLineRes(), exactly like those two.

   depthTest:false, and no depthWrite either: this is a check ON the render,
   not a citizen of it, and 3D content sitting between the eye and the grid's
   own plane — a recess wall, an object in front — would otherwise blank out
   exactly the part of the grid nearest whatever it's being used to judge.
   White rather than the earlier blue: it has to hold up against any
   backdrop the grid is checked against, not just the ones blue reads well
   on, and a diagnostic overlay is not the place to be precious about colour
   matching the rest of the workspace chrome. */
export const calibGridMat = new LineMaterial({
  color: 0xFFFFFF, linewidth: 4, depthTest: false, depthWrite: false, toneMapped: false
});
export const calibGrid = new LineSegments2(new LineSegmentsGeometry(), calibGridMat);
calibGrid.renderOrder = 9999;   // after every other transparent pass, so depthTest:false can't be undercut by draw order
calibGrid.visible = false;
scene.add(calibGrid);

let builtKey = null;

/* Half the grid's own width, in metres along ITS OWN local x — a billboard
   tilted relative to both panels, so this is a proportionate stand-in for
   "reaches each panel's outer edge", not an exact one; getting that exact
   would mean projecting each panel's far corner through the eye onto the
   grid's own plane, for a workspace aid that only has to look roughly
   right. Capped by the narrower panel so it's never drawn wider than the
   corner actually is on that side. */
function halfSpan(){
  return Math.min(S.screen.w, S.cornerPanel.w);
}

function rebuild(){
  const h = S.screen.h;
  const w = halfSpan() * 2;
  const COLUMNS = 10;
  /* Cells are exact squares, cell size set by the width alone — rows is
     however many of that SAME size it takes to at least cover the height,
     rounded up rather than fit to it. A grid that's true squares but runs a
     little past the top/bottom edge reads as a grid; one bent slightly
     tall or short to land exactly on the edge reads as the very distortion
     it exists to catch. */
  const cell = w / COLUMNS;
  const rows = Math.max(1, Math.ceil(h / cell));
  const x0 = -w/2, x1 = w/2, gh = rows * cell, y0 = -gh/2, y1 = gh/2;
  const pts = [];
  for(let i=0; i<=COLUMNS; i++){
    const x = x0 + i*cell;
    pts.push(x,y0,0, x,y1,0);
  }
  for(let j=0; j<=rows; j++){
    const y = y0 + j*cell;
    pts.push(x0,y,0, x1,y,0);
  }
  calibGrid.geometry.dispose();
  calibGrid.geometry = new LineSegmentsGeometry();
  calibGrid.geometry.setPositions(pts);
}

const _camPos = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _up = new THREE.Vector3(0,1,0);

/**
 * Called once per On-site frame, before either panel's render pass — see
 * the header comment for why once is enough. `cam` is whichever camera is
 * active (flatCam for On-site); only its POSITION is read, on purpose.
 */
export function updateCalibGrid(cam){
  const show = isSiteLike() && S.showCalibGrid && !!S.cornerPanel;
  calibGrid.visible = show;
  if(!show) return;

  const key = S.screen.w + ':' + S.screen.h + ':' + S.cornerPanel.w;
  if(key !== builtKey){ rebuild(); builtKey = key; }

  // Anchored at the seam's own midpoint — the hinge sits at x = screen.w/2,
  // z = 0 in world space (scene/panels.js's rightPanelDesc), y = 0 is the
  // screen's own vertical centre.
  calibGrid.position.set(S.screen.w/2, 0, 0);
  cam.getWorldPosition(_camPos);
  _m.lookAt(_camPos, calibGrid.position, _up);
  calibGrid.quaternion.setFromRotationMatrix(_m);
}
